import { Cartesian3, Color, createGuid } from "cesium";
import { getDefaultCenterLineSettings, getDefaultControlLineSettings, getDefaultPolylineSettings, } from "../../settings/earthbrain-site-settings";
import { AssetType, } from "../../types";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { BendType, PathSlopeType } from "../mid-path/interface";
import { ERubbingDestination } from "../trench/types";
import { Site } from "../site";
import { CurveDataType, HorizontalAlignmentType, } from "../../core/alignment/horizontal-alignment/interface";
import { getAlignmentDataFromControlPointsAndSegments } from "../../utilities/alignment-adapter-utils";
function fromOldRoadData(jsonstr, defaultPointCloudId) {
    const json = JSON.parse(jsonstr);
    if (json.controlPointData.length < 2) {
        throw new Error("Road data is empty or invalid");
    }
    const roadId = json.roadId;
    const roadSettings = RoadSavedData.fillEmptyByDefaultRoadSetting(RoadSavedData.fromOldRoadSettingsData(json.roadSettings)); //{ commonSetting: commonSetting, segmentSettings: [] };
    const controlPoints = json.controlPointData.map((p) => ({
        point: Cartesian3.fromArray(p.position),
        bendType: p.bendType === "arc" ? BendType.ARC : BendType.NONE,
    }));
    const associatePointCloudId = defaultPointCloudId;
    const surveyPoints = json.controlPointData.map((p, i) => ({
        wgs84Point: Cartesian3.fromArray(p.position),
        id: createGuid(),
        name: "IP " + i,
    }));
    const ipPointIds = surveyPoints.map((p) => p.id);
    const horizontalAlignmentData = [];
    // if there are straight, it can be single or multiple consecutive
    if (ipPointIds.length === 2) {
        horizontalAlignmentData.push({
            type: CurveDataType.LINEAR,
            startPointId: ipPointIds[0],
            endPointId: ipPointIds[1],
        });
    }
    else {
        const midPoint1 = Cartesian3.midpoint(surveyPoints[0].wgs84Point, surveyPoints[1].wgs84Point, new Cartesian3());
        const midPoint2 = Cartesian3.midpoint(surveyPoints[surveyPoints.length - 2].wgs84Point, surveyPoints[surveyPoints.length - 1].wgs84Point, new Cartesian3());
        const midPoint1_Survey = {
            wgs84Point: midPoint1,
            id: createGuid(),
            name: "BC",
        };
        const midPoint2_Survey = {
            wgs84Point: midPoint2,
            id: createGuid(),
            name: "EC",
        };
        surveyPoints.push(midPoint1_Survey);
        surveyPoints.push(midPoint2_Survey);
        horizontalAlignmentData.push({
            type: CurveDataType.LINEAR,
            startPointId: surveyPoints[0].id,
            endPointId: midPoint1_Survey.id,
        });
        horizontalAlignmentData.push({
            type: CurveDataType.QUADRATIC_BEZIER,
            pointIds: [
                midPoint1_Survey.id,
                ...ipPointIds.slice(1, ipPointIds.length - 1),
                midPoint2_Survey.id,
            ],
            startPointId: midPoint1_Survey.id,
            endPointId: midPoint2_Survey.id,
        });
        horizontalAlignmentData.push({
            type: CurveDataType.LINEAR,
            startPointId: midPoint2_Survey.id,
            endPointId: surveyPoints[surveyPoints.length - 1].id,
        });
    }
    return {
        roadId,
        roadSettings,
        surveyPoints,
        ipPointIds,
        horizontalAlignment: {
            type: HorizontalAlignmentType.QUADRATIC_BEZIER,
            data: horizontalAlignmentData,
        },
        verticalAlignment: [],
        associatedTerrain: {
            id: associatePointCloudId,
            terrainType: AssetType.Point_Cloud,
        },
        rubbingDestination: {
            target: ERubbingDestination.POINT_CLOUD,
            id: associatePointCloudId,
        },
    };
}
async function fromRoadData_Version1(jsonstr) {
    const site = Site.getInstance();
    const json = JSON.parse(jsonstr);
    const roadId = json.roadId;
    const roadSettings = RoadSavedData.fillEmptyByDefaultRoadSetting(json.roadSettings);
    // const controlPoints = json.controlPoints.map((p) => ({
    // 	point: Cartesian3.fromArray(p.point),
    // 	bendType: p.bendType === "arc" ? BendType.ARC : BendType.NONE,
    // }));
    const associatePointCloudId = json.associatePointCloudId;
    //slopeBreaks lower height by calculating the offset
    let offset = 0;
    const pointcloud = site.getPointCloud(associatePointCloudId);
    if (pointcloud) {
        const localPoints = pointcloud.heightMap
            .getVoxelCenters(true)
            .filter((c) => isFinite(c.z));
        if (localPoints && localPoints.length > 0) {
            const [worldCoordinate] = await site.convertPointsToWGS84([
                localPoints[0],
            ]);
            if (worldCoordinate.z !== worldCoordinate.z) {
                throw { message: "conversion error" };
            }
            const cesiumAltitude = EarthBrainUtils.getAltitude(new Cartesian3(worldCoordinate.x, worldCoordinate.y, worldCoordinate.z));
            offset = cesiumAltitude - localPoints[0].z;
        }
    }
    const { surveyPoints, ipPointIds, horizontalAlignmentData, verticalAlignmentData, } = await getAlignmentDataFromControlPointsAndSegments(json, offset);
    return {
        roadId,
        roadSettings,
        surveyPoints,
        ipPointIds,
        horizontalAlignment: horizontalAlignmentData,
        verticalAlignment: verticalAlignmentData,
        associatedTerrain: {
            id: associatePointCloudId,
            terrainType: AssetType.Point_Cloud,
        },
        rubbingDestination: {
            target: ERubbingDestination.POINT_CLOUD,
            id: associatePointCloudId,
        },
    };
}
async function fromRoadData_Version2(jsonstr) {
    const json = JSON.parse(jsonstr);
    const roadId = json.roadId;
    const roadSettings = RoadSavedData.fillEmptyByDefaultRoadSetting(json.roadSettings);
    if (!json.associatePointCloudId && !json.associateDrawing2dImageId) {
        throw new Error("Road data must have either associatePointCloudId or associateDrawing2dImageId");
    }
    const { surveyPoints, ipPointIds, horizontalAlignmentData, verticalAlignmentData, } = await getAlignmentDataFromControlPointsAndSegments(json);
    return {
        roadId,
        roadSettings,
        surveyPoints,
        ipPointIds,
        horizontalAlignment: horizontalAlignmentData,
        verticalAlignment: verticalAlignmentData,
        associatedTerrain: json.associatePointCloudId
            ? {
                id: json.associatePointCloudId,
                terrainType: AssetType.Point_Cloud,
            }
            : {
                id: json.associateDrawing2dImageId,
                terrainType: AssetType.REFERENCE_TOPOLOGY,
            },
        rubbingDestination: json.associatePointCloudId
            ? {
                id: json.associatePointCloudId,
                target: ERubbingDestination.POINT_CLOUD,
            }
            : {
                id: json.associateDrawing2dImageId,
                target: ERubbingDestination.REFERENCE_TOPOLOGY,
            },
    };
}
async function fromRoadData_Version3(jsonstr) {
    const json = JSON.parse(jsonstr);
    const roadId = json.roadId;
    const roadSettings = RoadSavedData.fillEmptyByDefaultRoadSetting(json.roadSettings);
    const { surveyPoints, ipPointIds, horizontalAlignmentData, verticalAlignmentData, } = await getAlignmentDataFromControlPointsAndSegments(json);
    return {
        roadId,
        roadSettings,
        surveyPoints,
        ipPointIds,
        horizontalAlignment: horizontalAlignmentData,
        verticalAlignment: verticalAlignmentData,
        associatedTerrain: {
            id: json.associatedTerrain.id,
            terrainType: json.associatedTerrain.terrainType,
        },
        rubbingDestination: json.rubbingDestination,
    };
}
export var RoadSavedData;
(function (RoadSavedData) {
    function fromOldRoadSettingsData(json) {
        if (typeof json === "string") {
            json = JSON.parse(json);
        }
        const commonCrossPath = {
            roadSlopeType: PathSlopeType.CROSS,
            leftSlope: json.commonSettings.leftSlope,
            rightSlope: json.commonSettings.rightSlope,
            leftWidth: json.commonSettings.leftWidth,
            rightWidth: json.commonSettings.rightWidth,
            interpolationPitch: json.commonSettings.interpolationPitch,
            color: json.commonSettings.surfaceColor,
            transparency: json.embarkmentSettings.transparency,
            outline: json.embarkmentSettings.outline,
            outlineColor: json.embarkmentSettings.outlineColor,
            controlLineSettings: json.embarkmentSettings.controlLineSettings,
            polyLineSettings: json.embarkmentSettings.polyLineSettings,
            centerLineSettings: json.embarkmentSettings.centerLineSettings,
        };
        const commonStraightPath = {
            roadSlopeType: PathSlopeType.STRAIGHT,
            slope: json.commonSettings.leftSlope,
            leftWidth: json.commonSettings.leftWidth,
            rightWidth: json.commonSettings.rightWidth,
            interpolationPitch: json.commonSettings.interpolationPitch,
            color: json.commonSettings.surfaceColor,
            transparency: json.embarkmentSettings.transparency,
            outline: json.embarkmentSettings.outline,
            outlineColor: json.embarkmentSettings.outlineColor,
            controlLineSettings: json.embarkmentSettings.controlLineSettings,
            polyLineSettings: json.embarkmentSettings.polyLineSettings,
            centerLineSettings: json.embarkmentSettings.centerLineSettings,
        };
        const commonSetting = {
            path: json.commonSettings.isSuperElevation
                ? commonStraightPath
                : commonCrossPath,
            cutSetting: {
                generateShelf: json.cutSettings.generateShelf,
                left: {
                    slopeHeight: json.cutSettings.bLeftRightSettings
                        ? json.cutSettings.leftSlopeHeight
                        : json.cutSettings.roadHeight,
                    moundSlope: json.cutSettings.bLeftRightSettings
                        ? json.cutSettings.leftMoundSlope
                        : json.cutSettings.moundSlope,
                    shelfWidth: json.cutSettings.bLeftRightSettings
                        ? json.cutSettings.leftShelfWidth
                        : json.cutSettings.shelfWidth,
                    shelfSlope: json.cutSettings.bLeftRightSettings
                        ? json.cutSettings.leftShelfSlope
                        : json.cutSettings.shelfSlope,
                },
                right: {
                    slopeHeight: json.cutSettings.bLeftRightSettings
                        ? json.cutSettings.rightSlopeHeight
                        : json.cutSettings.roadHeight,
                    moundSlope: json.cutSettings.bLeftRightSettings
                        ? json.cutSettings.rightMoundSlope
                        : json.cutSettings.moundSlope,
                    shelfWidth: json.cutSettings.bLeftRightSettings
                        ? json.cutSettings.rightShelfWidth
                        : json.cutSettings.shelfWidth,
                    shelfSlope: json.cutSettings.bLeftRightSettings
                        ? json.cutSettings.rightShelfSlope
                        : json.cutSettings.shelfSlope,
                },
                color: json.cutSettings.color,
                transparency: json.cutSettings.transparency,
            },
            embarkmentSetting: {
                generateShelf: json.embarkmentSettings.generateShelf,
                left: {
                    slopeHeight: json.embarkmentSettings.bLeftRightSettings
                        ? json.embarkmentSettings.leftSlopeHeight
                        : json.embarkmentSettings.roadHeight,
                    moundSlope: json.embarkmentSettings.bLeftRightSettings
                        ? json.embarkmentSettings.leftMoundSlope
                        : json.embarkmentSettings.moundSlope,
                    shelfWidth: json.embarkmentSettings.bLeftRightSettings
                        ? json.embarkmentSettings.leftShelfWidth
                        : json.embarkmentSettings.shelfWidth,
                    shelfSlope: json.embarkmentSettings.bLeftRightSettings
                        ? json.embarkmentSettings.leftShelfSlope
                        : json.embarkmentSettings.shelfSlope,
                },
                right: {
                    slopeHeight: json.embarkmentSettings.bLeftRightSettings
                        ? json.embarkmentSettings.rightSlopeHeight
                        : json.embarkmentSettings.roadHeight,
                    moundSlope: json.embarkmentSettings.bLeftRightSettings
                        ? json.embarkmentSettings.rightMoundSlope
                        : json.embarkmentSettings.moundSlope,
                    shelfWidth: json.embarkmentSettings.bLeftRightSettings
                        ? json.embarkmentSettings.rightShelfWidth
                        : json.embarkmentSettings.shelfWidth,
                    shelfSlope: json.embarkmentSettings.bLeftRightSettings
                        ? json.embarkmentSettings.rightShelfSlope
                        : json.embarkmentSettings.shelfSlope,
                },
                color: json.embarkmentSettings.color,
                transparency: json.embarkmentSettings.transparency,
            },
        };
        if (!json.cutSettings.bLeftRightSettings) {
            commonSetting.cutSetting.right = { ...commonSetting.cutSetting.left };
        }
        if (!json.embarkmentSettings.bLeftRightSettings) {
            commonSetting.embarkmentSetting.right = {
                ...commonSetting.embarkmentSetting.left,
            };
        }
        const roadSettings = { commonSetting };
        return roadSettings;
    }
    RoadSavedData.fromOldRoadSettingsData = fromOldRoadSettingsData;
    function fillEmptyByDefaultRoadSetting(roadSetting) {
        const fillCommonSettings = (roadCommonSetting) => {
            const pathCommonSetting = {
                color: roadCommonSetting.path.color ?? "#DDDDDD",
                transparency: roadCommonSetting.path.transparency ?? 1,
                leftWidth: roadCommonSetting.path.leftWidth ?? 2.5,
                rightWidth: roadCommonSetting.path.rightWidth ?? 2.5,
                interpolationPitch: roadCommonSetting.path.interpolationPitch ?? 5,
                outline: roadCommonSetting.path.outline ?? true,
                outlineColor: roadCommonSetting.path.outlineColor ?? Color.BLACK,
                controlLineSettings: roadCommonSetting.path.controlLineSettings ??
                    getDefaultControlLineSettings(),
                polyLineSettings: roadCommonSetting.path.polyLineSettings ??
                    getDefaultPolylineSettings(),
                centerLineSettings: roadCommonSetting.path.centerLineSettings ??
                    getDefaultCenterLineSettings(),
            };
            const pathWithRoadSlope = {
                ...pathCommonSetting,
            };
            pathWithRoadSlope.roadSlopeType =
                roadCommonSetting.path.roadSlopeType ?? PathSlopeType.CROSS;
            if (pathWithRoadSlope.roadSlopeType === PathSlopeType.CROSS) {
                pathWithRoadSlope.leftSlope =
                    roadCommonSetting.path.leftSlope ?? 50;
                pathWithRoadSlope.rightSlope =
                    roadCommonSetting.path.rightSlope ?? 50;
            }
            else if (pathWithRoadSlope.roadSlopeType === PathSlopeType.STRAIGHT) {
                pathWithRoadSlope.slope =
                    roadCommonSetting.path.slope ?? 50;
            }
            const commonSetting = {
                path: pathWithRoadSlope,
                cutSetting: {
                    generateShelf: roadCommonSetting.cutSetting.generateShelf ?? true,
                    left: {
                        slopeHeight: roadCommonSetting.cutSetting.left.slopeHeight ?? 5,
                        moundSlope: roadCommonSetting.cutSetting.left.moundSlope ?? 1.5,
                        shelfWidth: roadCommonSetting.cutSetting.left.shelfWidth ?? 1.5,
                        shelfSlope: roadCommonSetting.cutSetting.left.shelfSlope ?? 100,
                    },
                    right: {
                        slopeHeight: roadCommonSetting.cutSetting.right.slopeHeight ?? 5,
                        moundSlope: roadCommonSetting.cutSetting.right.moundSlope ?? 1.5,
                        shelfWidth: roadCommonSetting.cutSetting.right.shelfWidth ?? 1.5,
                        shelfSlope: roadCommonSetting.cutSetting.right.shelfSlope ?? 100,
                    },
                    color: roadCommonSetting.cutSetting.color ?? "#FFA500",
                    transparency: roadCommonSetting.cutSetting.transparency ?? 1,
                },
                embarkmentSetting: {
                    generateShelf: roadCommonSetting.embarkmentSetting.generateShelf ?? true,
                    left: {
                        slopeHeight: roadCommonSetting.embarkmentSetting.left.slopeHeight ?? 5,
                        moundSlope: roadCommonSetting.embarkmentSetting.left.moundSlope ?? 1.5,
                        shelfWidth: roadCommonSetting.embarkmentSetting.left.shelfWidth ?? 1.5,
                        shelfSlope: roadCommonSetting.embarkmentSetting.left.shelfSlope ?? 100,
                    },
                    right: {
                        slopeHeight: roadCommonSetting.embarkmentSetting.right.slopeHeight ?? 5,
                        moundSlope: roadCommonSetting.embarkmentSetting.right.moundSlope ?? 1.5,
                        shelfWidth: roadCommonSetting.embarkmentSetting.right.shelfWidth ?? 1.5,
                        shelfSlope: roadCommonSetting.embarkmentSetting.right.shelfSlope ?? 100,
                    },
                    color: roadCommonSetting.embarkmentSetting.color ?? "#008000",
                    transparency: roadCommonSetting.embarkmentSetting.transparency ?? 1,
                },
            };
            return commonSetting;
        };
        const commonSetting = fillCommonSettings(roadSetting.commonSetting);
        const roadSettings = { commonSetting };
        return roadSettings;
    }
    RoadSavedData.fillEmptyByDefaultRoadSetting = fillEmptyByDefaultRoadSetting;
    async function parseRoadSavedData(jsonString, defaultPointCloudId) {
        let roadData;
        const jsonData = JSON.parse(jsonString);
        switch (jsonData.version) {
            case undefined:
                roadData = fromOldRoadData(jsonString, defaultPointCloudId);
                break;
            case 1.0:
                roadData = await fromRoadData_Version1(jsonString);
                break;
            case 2.0:
                roadData = await fromRoadData_Version2(jsonString);
                break;
            case 3.0:
                roadData = await fromRoadData_Version3(jsonString);
                break;
            default:
                throw new Error(`Unknown road data version: ${jsonData.version}`);
        }
        return roadData;
    }
    RoadSavedData.parseRoadSavedData = parseRoadSavedData;
})(RoadSavedData || (RoadSavedData = {}));
//# sourceMappingURL=roadData.js.map